import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../chat/api";

/* Every requirement in a project, in the conversation.
 *
 * A hundred-odd requirements is a genuinely tiring thing to read, and the
 * failure mode is not "too little information" — it is a wall of identical
 * sentences that nobody finishes. Three things fix that here:
 *
 *   SHAPE FIRST. The card opens showing category names and counts, so the
 *   reader learns "81 of these are scope, 13 are forms" before reading a single
 *   requirement. That is the answer to most questions on its own.
 *
 *   NOTHING OPENS BY ITSELF. Groups start closed. Opening one is a decision to
 *   read it, which is why the eye is willing to.
 *
 *   FIND, DON'T SCROLL. The filter matches across every category and opens the
 *   ones that hit, so looking for "SSO" or "turnover" takes one keystroke
 *   instead of five expansions.
 *
 * The text is fetched per project on first open, never carried in the chat row
 * — see the consumer's card payload, which is counts only.
 */

/* Colour per category, so a group is recognisable before it is read and the
 * same category looks the same every time. Spelled out rather than derived at
 * runtime: Tailwind emits only the class names it can see in source. */
const CATEGORY_TONE = {
  "Scope of Work":              { dot: "bg-[#A04A97]", ring: "ring-[#A04A97]/25" },
  "Compliance & Legal":         { dot: "bg-[#B91C1C]", ring: "ring-[#B91C1C]/25" },
  "Required Forms & Appendices": { dot: "bg-[#92620B]", ring: "ring-[#92620B]/25" },
  "Commercial":                 { dot: "bg-[#1F7D38]", ring: "ring-[#1F7D38]/25" },
  "Deliverables & Timeline":    { dot: "bg-[#C86B4F]", ring: "ring-[#C86B4F]/25" },
  "Evaluation Criteria":        { dot: "bg-[#5C7326]", ring: "ring-[#5C7326]/25" },
};
const toneOf = (name) => CATEGORY_TONE[name] || { dot: "bg-[#6B6472]", ring: "ring-[#6B6472]/25" };

const Chevron = ({ open }) => (
  <svg
    aria-hidden="true"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-none transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-90" : ""}`}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/* Highlights the matched run so the reason a row survived the filter is
 * visible, rather than leaving the reader to re-find it by eye. */
const Highlight = ({ text, term }) => {
  if (!term) return text;
  const i = text.toLowerCase().indexOf(term.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-[3px] bg-[#A04A97]/[0.18] px-[1px] text-[#18101C]">
        {text.slice(i, i + term.length)}
      </mark>
      {text.slice(i + term.length)}
    </>
  );
};

/* The extraction pipeline's real stages, in the order it runs them.
 * Published by the pipeline itself (see _publish_phase), so a step that shows
 * as finished has genuinely finished — none of this is on a timer. The primary
 * pass is by far the longest, which is why it earns its own line rather than
 * hiding inside one "working". */
const PHASES = [
  { key: "reading",    label: "Reading the document" },
  { key: "extracting", label: "Pulling out requirements" },
  { key: "checking",   label: "Checking for anything missed" },
  { key: "tidying",    label: "Removing duplicates" },
];

/* One document's line in the progress view. */
const DOC_STATE = {
  done:    { label: "Read",            dot: "bg-[#2DAD4E]", text: "text-[#1F7D38]" },
  working: { label: "Reading it now",  dot: "bg-[#A04A97]", text: "text-[#8E4187]" },
  queued:  { label: "Waiting its turn", dot: "bg-[#B4AEBA]", text: "text-[#6B6472]" },
  waiting: { label: "Still processing", dot: "bg-[#B4AEBA]", text: "text-[#6B6472]" },
  failed:  { label: "Couldn't be read", dot: "bg-[#DC2626]", text: "text-[#B91C1C]" },
};

/* Reads the SAME endpoint the project's requirements tab reads.
 *
 * There was briefly a copilot-only endpoint for this. It was a second reader of
 * one dataset, which is how a card and a page end up disagreeing about how many
 * requirements a project has — so it was deleted and this shapes the existing
 * response instead. `requirements_phase` is the one field added there, additive
 * and ignored by every other consumer.
 */
const readProject = async (projectId) => {
  const res = await api.get(`/api/v1/rfp/folders/${projectId}/requirements/alignment/`);
  const docs = res?.data?.documents || [];

  const rows = [];
  const categories = {};
  const documents = [];

  for (const d of docs) {
    const byCat = d.requirements_by_category || {};
    let n = 0;
    for (const [cat, list] of Object.entries(byCat)) {
      for (const r of list || []) {
        const text = (r?.text || "").trim();
        if (!text) continue;
        rows.push({ text, category: cat, document: d.doc_name });
        categories[cat] = (categories[cat] || 0) + 1;
        n += 1;
      }
    }

    const raw = (d.insights_status_raw || "").toLowerCase();
    const queued = raw === "pending" || raw === "processing";
    const state = n > 0
      ? "done"
      : queued
      ? "working"
      : raw === "failed" || raw === "not_extracted"
      ? "failed"
      : "queued";
    documents.push({
      name: d.doc_name,
      state,
      requirements: n,
      phase: d.requirements_phase || "reading",
    });
  }

  // Extraction is only "running" while a document says so — a project that has
  // simply never been extracted is not the same thing, and must not leave the
  // card polling forever.
  const state = documents.some((d) => d.state === "working") ? "extracting" : "ready";
  return { state, total: rows.length, categories, documents, requirements: rows };
};

const CopilotRequirementsCard = ({ message }) => {
  const { projectId, projectName } = message;

  /* The card owns its own picture of the project, seeded from the payload the
   * turn carried. While extraction runs it re-reads the endpoint on a timer, so
   * one card goes from "reading document 1 of 3" to the finished list without
   * the user asking twice. */
  const [live, setLive] = useState({
    state: message.state || "ready",
    total: message.total || 0,
    categories: message.categories || {},
    documents: message.documents || [],
  });
  const { total, categories } = live;
  const busy = live.state === "extracting" || live.state === "started_extraction";

  const [open, setOpen] = useState(() => new Set());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState("");
  const loadedRef = useRef(false);

  const order = useMemo(
    () => Object.entries(categories || {}).sort((a, b) => b[1] - a[1]),
    [categories]
  );

  /* Fetched once, for the whole project, the first time anything needs text.
   * Per-category requests would be more requests for less: the payload is one
   * list either way, and searching across categories needs all of it anyway. */
  const ensureRows = useCallback(async () => {
    if (loadedRef.current || loading) return;
    loadedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const d = await readProject(projectId);
      setRows(d.requirements);
    } catch {
      loadedRef.current = false; // let a retry happen
      setError("Couldn't load the requirements.");
    } finally {
      setLoading(false);
    }
  }, [projectId, loading]);

  /* Polls only while there is something to wait for, and stops the moment the
   * requirements exist. A timer left running after the work finishes is the
   * quiet kind of bug that only shows up as load on the server. */
  useEffect(() => {
    if (!busy) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const d = await readProject(projectId);
        if (!alive) return;
        setLive({
          state: d.state,
          total: d.total,
          categories: d.categories,
          documents: d.documents,
        });
        if (d.state === "ready") {
          // The rows arrived with this very response; keep them rather than
          // making the first expand pay for another round trip.
          setRows(d.requirements);
          loadedRef.current = true;
        }
      } catch {
        /* a missed poll is not worth surfacing — the next one will do */
      }
    };
    const id = setInterval(tick, 4000);
    tick();
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [busy, projectId]);

  // Typing needs the text before it can match anything.
  useEffect(() => {
    if (term.trim()) ensureRows();
  }, [term, ensureRows]);

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q || !rows) return null;
    return rows.filter((r) => r.text.toLowerCase().includes(q));
  }, [rows, term]);

  const matchCounts = useMemo(() => {
    if (!matches) return null;
    return matches.reduce((acc, r) => {
      acc[r.category] = (acc[r.category] || 0) + 1;
      return acc;
    }, {});
  }, [matches]);

  const toggle = async (name) => {
    const next = new Set(open);
    if (next.has(name)) next.delete(name);
    else {
      next.add(name);
      ensureRows();
    }
    setOpen(next);
  };

  const allOpen = order.length > 0 && open.size === order.length;
  const toggleAll = () => {
    if (allOpen) setOpen(new Set());
    else {
      setOpen(new Set(order.map(([name]) => name)));
      ensureRows();
    }
  };

  // While filtering, a group is open if it has a hit — otherwise the reader
  // would have to expand groups to discover the search found nothing in them.
  const isOpen = (name) => (matchCounts ? !!matchCounts[name] : open.has(name));
  const rowsFor = (name) =>
    (matches || rows || []).filter((r) => r.category === name);

  const shownTotal = matches ? matches.length : total;

  return (
    <div className="max-w-[640px] overflow-hidden rounded-2xl border border-[#18101C]/[0.08] bg-white shadow-[0_1px_2px_rgba(24,16,28,0.04),0_12px_28px_-18px_rgba(24,16,28,0.22)]">
      <div className="border-b border-[#18101C]/[0.06] px-4 pb-3 pt-3.5">
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-[14.5px] font-semibold tracking-[-0.01em] text-[#18101C]">
            Requirements
            {projectName && (
              <span className="ml-1.5 font-normal text-[#6B6472]">· {projectName}</span>
            )}
          </p>
          <span className="flex-none text-[12.5px] tabular-nums text-[#6B6472]">
            {busy ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#A04A97]/[0.09] px-2 py-[2px] text-[11.5px] font-semibold text-[#8E4187]">
                <span className="h-[5px] w-[5px] rounded-full bg-[#A04A97] animate-pulse motion-reduce:animate-none" />
                Extracting
              </span>

            ) : matches ? (
              <>
                <span className="font-semibold text-[#8E4187]">{shownTotal}</span> of {total}
              </>
            ) : (
              <>
                <span className="font-semibold text-[#18101C]">{total}</span> in total
              </>
            )}
          </span>
        </div>

        <div className={`mt-2.5 flex items-center gap-2 ${busy ? "hidden" : ""}`}>
          <div className="relative min-w-0 flex-1">
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute left-[9px] top-1/2 -translate-y-1/2 text-[#6E6775]"
              width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              aria-label="Filter requirements"
              placeholder="Filter — try SSO, turnover, annexure"
              className="w-full rounded-lg border border-transparent bg-[#18101C]/[0.045] py-[7px] pl-[30px] pr-7 text-[13px] text-[#18101C] outline-none transition-colors placeholder:text-[#6E6775] focus:border-[#A04A97]/50 focus:bg-white motion-reduce:transition-none"
            />
            {term && (
              <button
                type="button"
                onClick={() => setTerm("")}
                aria-label="Clear filter"
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[#6E6775] hover:text-[#18101C]"
              >
                <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={toggleAll}
            disabled={!!matchCounts}
            title={matchCounts ? "Groups follow the filter while it is on" : undefined}
            className="flex-none rounded-lg px-2 py-[7px] text-[12.5px] font-semibold text-[#A04A97] transition-colors hover:bg-[#A04A97]/[0.09] hover:text-[#8E4187] disabled:cursor-default disabled:text-[#9C96A2] disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>
      </div>

      {busy && (
        <div className="px-4 pb-4 pt-3.5">
          {(live.documents || []).map((d, i) => {
            const tone = DOC_STATE[d.state] || DOC_STATE.queued;
            const at = PHASES.findIndex((p) => p.key === d.phase);
            const working = d.state === "working";
            return (
              <div key={`${d.name}-${i}`} className={i ? "mt-4" : ""}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="min-w-0 truncate text-[13.5px] font-medium text-[#18101C]" title={d.name}>
                    {d.name}
                  </p>
                  {working ? (
                    <span className="flex-none text-[11.5px] font-semibold tabular-nums text-[#8E4187]">
                      Step {Math.max(at, 0) + 1} of {PHASES.length}
                    </span>
                  ) : (
                    <span className={`flex-none text-[11.5px] ${tone.text}`}>
                      {tone.label}
                      {d.state === "done" && d.requirements > 0 && ` · ${d.requirements}`}
                    </span>
                  )}
                </div>

                {working && (
                  <>
                    {/* A segment per stage, filling as each completes. Four real
                        boundaries, so a filled segment means a finished stage —
                        not a bar creeping on a timer. The live segment carries
                        the sweep, so motion sits exactly where the work is. */}
                    <div className="mt-2 flex gap-1" aria-hidden="true">
                      {PHASES.map((ph, pi) => (
                        <span
                          key={ph.key}
                          className={`h-[3px] flex-1 overflow-hidden rounded-full ${
                            at > pi ? "bg-[#A04A97]" : "bg-[#18101C]/[0.07]"
                          }`}
                        >
                          {at === pi && (
                            <span className="block h-full w-1/2 rounded-full bg-[#A04A97] animate-copilot-sweep motion-reduce:animate-none motion-reduce:w-full" />
                          )}
                        </span>
                      ))}
                    </div>

                    <ol className="mt-2.5 space-y-1">
                      {PHASES.map((ph, pi) => {
                        const done = at > pi;
                        const now = at === pi;
                        if (!done && !now) return null;
                        return (
                          <li
                            key={ph.key}
                            className={`flex items-center gap-2 text-[12.5px] leading-snug ${
                              now ? "font-semibold text-[#18101C]" : "text-[#6B6472]"
                            }`}
                          >
                            {done ? (
                              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1F7D38" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" className="flex-none">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <span className="relative flex h-3 w-3 flex-none items-center justify-center">
                                <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-[#A04A97] opacity-50 motion-reduce:animate-none" />
                                <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#A04A97]" />
                              </span>
                            )}
                            {ph.label}
                          </li>
                        );
                      })}
                    </ol>
                  </>
                )}
              </div>
            );
          })}

          <p role="status" className="mt-3.5 text-[12px] text-[#6E6775]">
            This fills in on its own.
          </p>
        </div>
      )}

      <div className={`max-h-[52vh] overflow-y-auto sensa-scroll ${busy ? "hidden" : ""}`}>
        {error && (
          <p role="alert" className="px-4 py-3 text-[13px] text-[#B91C1C]">{error}</p>
        )}

        {matches && matches.length === 0 && !loading && (
          <p className="px-4 py-6 text-center text-[13px] text-[#6B6472]">
            Nothing matches “{term}”.
          </p>
        )}

        <ul className="divide-y divide-[#18101C]/[0.05]">
          {order.map(([name, count]) => {
            const tone = toneOf(name);
            const shown = matchCounts ? matchCounts[name] : count;
            if (matchCounts && !shown) return null;
            const opened = isOpen(name);
            const items = opened ? rowsFor(name) : [];

            return (
              <li key={name}>
                <button
                  type="button"
                  onClick={() => toggle(name)}
                  disabled={!!matchCounts}
                  aria-expanded={opened}
                  className="flex w-full items-center gap-2.5 px-4 py-[11px] text-left transition-colors hover:bg-[#18101C]/[0.025] disabled:cursor-default disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#A04A97] motion-reduce:transition-none"
                >
                  <span className="text-[#6B6472]">
                    <Chevron open={opened} />
                  </span>
                  <span className={`h-[7px] w-[7px] flex-none rounded-full ${tone.dot}`} aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-[#18101C]">
                    {name}
                  </span>
                  <span className="flex-none text-[12.5px] tabular-nums text-[#6B6472]">{shown}</span>
                </button>

                {opened && (
                  <div className="pb-2 pl-[42px] pr-4">
                    {loading && !items.length ? (
                      <p className="flex items-center gap-2 py-2 text-[12.5px] text-[#6B6472]">
                        <span className="h-3 w-3 flex-none animate-spin rounded-full border border-[#A04A97]/30 border-t-[#A04A97] motion-reduce:animate-none" />
                        Reading them out…
                      </p>
                    ) : (
                      <ol className="space-y-1.5">
                        {items.map((r, i) => (
                          <li
                            key={`${name}-${i}`}
                            className={`rounded-lg py-1.5 pl-2.5 pr-2 text-[13px] leading-[1.55] text-[#3F3F46] ring-1 ring-inset ${tone.ring} bg-[#FCFBFC]`}
                          >
                            <Highlight text={r.text} term={term.trim()} />
                            {r.document && (
                              <span className="mt-1 block truncate text-[11.5px] text-[#6E6775]">
                                {r.document}
                              </span>
                            )}
                          </li>
                        ))}
                        {!items.length && !loading && (
                          <li className="py-2 text-[12.5px] text-[#6B6472]">
                            Nothing to show for this group.
                          </li>
                        )}
                      </ol>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default CopilotRequirementsCard;
